import { Injectable } from '@angular/core';
import { hide } from '@popperjs/core';

export interface NavigationItem {
  id: string;
  title: string;
  type: 'item' | 'collapse' | 'group';
  translate?: string;
  icon?: string;
  hidden?: boolean;
  url?: string;
  classes?: string;
  exactMatch?: boolean;
  external?: boolean;
  target?: boolean;
  breadcrumbs?: boolean;
  function?: any;
  role?: 'admin' | 'contact';
  /** Optional RBAC permission code; nav item is hidden when user lacks this permission. */
  permission?: string;
  /** When `role` is `contact`: show only if the user's groups include at least one of these (e.g. `adminCustomer`). */
  contactGroupsAny?: string[];
  /** When `role` is `contact`: hide if the user's groups include any of these. */
  contactGroupsNoneOf?: string[];
  /** Hide when portal quotation setting is disabled. */
  requiresQuotationEnabled?: boolean;
  /** Show only in the phone drawer (≤430px). Used for fixed-navbar shortcuts. */
  mobileOnly?: boolean;
  badge?: {
    title?: string;
    type?: string;
  };
  children?: Navigation[];
}

export interface Navigation extends NavigationItem {
  children?: NavigationItem[];
}

/** Nav tree data. Omits the injectable class `get()` from the merged `NavigationItem` type. */
export type NavConfig = Omit<NavigationItem, 'get' | 'children'> & {
  children?: NavConfig[];
};

const NavigationItems: NavConfig[] = [
  {
    id: 'group-main',
    title: 'SIDEBAR_GROUP_MAIN',
    type: 'group',
    icon: 'icon-navigation',
    children: [
      {
        id: 'cf-dashboard-contact',
        title: 'MAIN_DASHBOARD',
        type: 'item',
        role: 'contact',
        icon: 'home',
        url: '/customer/CFDashboard/dashboard',
        contactGroupsNoneOf: ['adminCustomer'],
      },
      {
        id: 'cf-dashboard-customer-admin',
        title: 'MAIN_DASHBOARD',
        type: 'item',
        role: 'contact',
        icon: 'home',
        url: '/customer/CFDashboard/admin-customer-dashboard',
        contactGroupsAny: ['adminCustomer'],
      },
      {
        id: 'cf',
        title: 'MAIN_DASHBOARD',
        type: 'item',
        role: 'admin',
        icon: 'home',
        url: '/admin/cf-dashboard',
      },
      {
        id: 'ac-cf-product',
        title: 'CATEGORY_MANAGEMENT',
        type: 'item',
        role: 'contact',
        icon: 'file',
        url: '/admin/cf-product/navigation-product',
        breadcrumbs: true,
        permission: 'cf_category.view_cf_category',
        contactGroupsAny: ['adminCustomer'],
      },
      {
        id: 'ac-bp-customers',
        title: 'BUSINESS_PARTNER',
        type: 'item',
        role: 'contact',
        icon: 'users',
        url: '/admin/cp-bp-management/cp-customer/customer-list',
        breadcrumbs: true,
        permission: 'cp_customer.view_cp_customer',
        contactGroupsAny: ['adminCustomer'],
      },
      {
        id: 'ac-cp-pricing',
        title: 'PRICING',
        type: 'item',
        role: 'contact',
        icon: 'dollar-sign',
        url: '/admin/cp-pricing/price-book-list',
        breadcrumbs: true,
        permission: 'cp_pricebook.view_cp_pricebook',
        contactGroupsAny: ['adminCustomer'],
      },
      {
        id: 'cf',
        title: 'PRODUCT',
        type: 'item',
        role: 'contact',
        icon: 'box',
        url: '/customer/CFProduct/product-view',
        breadcrumbs: true,
        permission: 'cf_item.view_cf_item',
      },
      {
        id: 'cf-detail',
        title: 'PRODUCT_DETAILS',
        type: 'item',
        role: 'contact',
        icon: 'folder',
        url: '/customer/CFDashboard/detail-product',
        breadcrumbs: true,
        hidden: true,
      },
      {
        id: 'cf-news',
        title: 'CFDashboard.News',
        type: 'item',
        role: 'contact',
        icon: 'folder',
        url: '/customer/CFProduct/product-news',
        breadcrumbs: true,
        hidden: true,
      },
      {
        id: 'cf-all',
        title: 'CFDashboard.ALL',
        type: 'item',
        role: 'contact',
        icon: 'folder',
        url: '/customer/CFProduct/all',
        breadcrumbs: true,
        hidden: true,
      },
      {
        id: 'cf-category',
        title: 'CFDashboard.Categories',
        type: 'item',
        role: 'contact',
        icon: 'folder',
        url: '/category',
        breadcrumbs: true,
        hidden: true,
      },
      {
        id: 'cf-compare',
        title: 'CPSalesorderData.Compare',
        type: 'item',
        role: 'contact',
        mobileOnly: true,
        icon: 'repeat',
        url: '/compare',
        breadcrumbs: true,
      },
      {
        id: 'cp-quotation-contact',
        title: 'CPSalesorderData.Quotation',
        type: 'item',
        role: 'contact',
        mobileOnly: true,
        icon: 'file-text',
        url: '/customer/quotation/quotation-list',
        breadcrumbs: true,
        requiresQuotationEnabled: true,
      },
      {
        id: 'cf-favorite-products',
        title: 'Favorite_Products',
        type: 'item',
        role: 'contact',
        mobileOnly: true,
        icon: 'star',
        url: '/customer/favorite-products',
        breadcrumbs: true,
      },
      {
        id: 'cf',
        title: 'PRODUCT_INFORMATION',
        type: 'item',
        role: 'admin',
        icon: 'box',
        url: '/admin/cf-product/navigation-product',
        breadcrumbs: true,
      },
      {
        id: 'cf',
        title: 'CfImages.title',
        type: 'item',
        role: 'admin',
        icon: 'image',
        url: '/admin/cf-images',
        breadcrumbs: true,
      },
    ],
  },
  {
    id: 'group-partners',
    title: 'SIDEBAR_GROUP_PARTNERS',
    type: 'group',
    icon: 'icon-navigation',
    children: [
      {
        id: 'cf',
        title: 'BUSINESS_PARTNER',
        type: 'item',
        role: 'admin',
        icon: 'users',
        url: '/admin/cp-bp-management/cp-customer/customer-list',
        breadcrumbs: true,
      },
      {
        id: 'cf',
        title: 'CONTACT_LIST',
        type: 'item',
        role: 'admin',
        icon: 'file',
        url: '/admin/cp-bp-management/contact-list',
        breadcrumbs: true,
        hidden: true,
      },
      {
        id: 'cp-site',
        title: 'SITE_MANAGEMENT',
        type: 'item',
        role: 'admin',
        icon: 'building',
        url: '/admin/cp-site/site-list',
        breadcrumbs: true,
      },
      {
        id: 'cp-warehouse',
        title: 'WAREHOUSE_MANAGEMENT',
        type: 'item',
        role: 'admin',
        icon: 'package',
        url: '/admin/warehouse/warehouse-list',
        breadcrumbs: true,
        hidden: true,
      },
      {
        id: 'permission-group-role',
        title: 'CFPermissions.RoleGroups',
        type: 'item',
        role: 'admin',
        icon: 'users',
        url: '/admin/permission/group/role',
        breadcrumbs: true,
        hidden: true,
      },
      {
        id: 'permission-group-user',
        title: 'CFPermissions.UserGroups',
        type: 'item',
        role: 'admin',
        icon: 'users',
        url: '/admin/permission/group/user',
        breadcrumbs: true,
        hidden: true,
      },
      {
        id: 'permission-group-create',
        title: 'CFPermissions.CreateGroup',
        type: 'item',
        role: 'admin',
        icon: 'plus',
        url: '/admin/permission/group-create',
        breadcrumbs: true,
        hidden: true,
      },
      {
        id: 'permission-group-edit',
        title: 'CFPermissions.EditGroup',
        type: 'item',
        role: 'admin',
        icon: 'edit',
        url: '/admin/permission/group-edit',
        breadcrumbs: true,
        hidden: true,
      },
      {
        id: 'permission-business-partner',
        title: 'CFPermissions.BusinessPartner',
        type: 'item',
        role: 'admin',
        icon: 'briefcase',
        url: '/admin/permission/businessPartner',
        breadcrumbs: true,
        hidden: true,
      },
      {
        id: 'permission-user-list',
        title: 'CFPermissions.UserList',
        type: 'item',
        role: 'admin',
        icon: 'users',
        url: '/admin/permission/user-list',
        breadcrumbs: true,
        hidden: true,
      },
      {
        id: 'permission-user-create',
        title: 'CFPermissions.UserPermission',
        type: 'item',
        role: 'admin',
        icon: 'user-plus',
        url: '/admin/permission/user-create',
        breadcrumbs: true,
        hidden: true,
      },
    ],
  },
  {
    id: 'group-sales',
    title: 'SIDEBAR_GROUP_SALES',
    type: 'group',
    icon: 'icon-navigation',
    children: [
      {
        id: 'cp',
        title: 'Sales_Order',
        type: 'item',
        role: 'contact',
        icon: 'clipboard',
        url: '/customer/cp-order/orders',
        breadcrumbs: true,
        permission: 'cp_salesorder.view_cp_salesorder',
      },
      {
        id: 'cp',
        title: 'Shipment',
        type: 'item',
        role: 'contact',
        icon: 'truck',
        url: '/customer/cp-shipment/shipments',
        breadcrumbs: true,
        permission: 'cp_shipment.view_cp_shipment',
      },
      {
        id: 'cp',
        title: 'Invoice',
        type: 'item',
        role: 'contact',
        icon: 'file-text',
        url: '/customer/invoice/CpInvoiceList',
        breadcrumbs: true,
        permission: 'cp_invoice.view_cp_invoice',
      },
      {
        id: 'cp-ticket',
        title: 'TICKET_MANAGEMENT',
        type: 'item',
        role: 'admin',
        icon: 'message-square',
        url: '/admin/cp-ticket',
        breadcrumbs: true,
      },
      {
        id: 'cp-quotation',
        title: 'CPAdminQuotation.nav_title',
        type: 'item',
        role: 'admin',
        icon: 'file-text',
        url: '/admin/cp-quotation',
        breadcrumbs: true,
        requiresQuotationEnabled: true,
      },
      {
        id: 'cp',
        title: 'Sales_Order',
        type: 'item',
        role: 'admin',
        icon: 'clipboard',
        url: '/admin/cp-sales-orders/sales-order-list',
        breadcrumbs: true,
        permission: 'cp_salesorder.view_cp_salesorder',
      },
      {
        id: 'cp',
        title: 'PRICING',
        type: 'item',
        role: 'admin',
        icon: 'dollar-sign',
        url: '/admin/cp-pricing/price-book-list',
        breadcrumbs: true,
        permission: 'cp_pricing.view_cp_pricing',
      },
    ],
  },
];

@Injectable()
export class NavigationItem {
  get(): NavConfig[] {
    return NavigationItems;
  }
}
